import React, { useState, useRef, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './button';
import { Input } from './input';
import { toast } from 'react-hot-toast';
import { useVariantCategories, useUpmVariantValues } from '../../hooks/useUpmMasters';
import api from '../../lib/api';
import { useQueryClient } from '@tanstack/react-query';

interface VariantCategory {
  id: string;
  name: string;
}

interface VariantValue {
  id: string;
  name: string;
  variant_category_id: string;
}

export interface VariantPair {
  category: VariantCategory;
  value: VariantValue;
}

interface VariantInputProps {
  value: VariantPair[];
  onChange: (variants: VariantPair[]) => void;
  className?: string;
  disabled?: boolean;
  error?: string;
}

const VariantInput: React.FC<VariantInputProps> = ({
  value = [],
  onChange,
  className,
  disabled = false,
  error,
}) => {
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newValueName, setNewValueName] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showAddValue, setShowAddValue] = useState(false);
  const queryClient = useQueryClient();

  // Use real API hooks
  const { data: categories = [], isLoading: categoriesLoading, error: categoriesError, refetch: refetchCategories } = useVariantCategories();
  const { data: values = [], isLoading: valuesLoading, error: valuesError, refetch: refetchValues } = useUpmVariantValues(
    selectedCategoryId || '',
    undefined,
    !!selectedCategoryId
  );


  // Add new category
  const addCategory = async (name: string): Promise<VariantCategory | null> => {
    try {
      const response = await api.createUpmMasters('variant_category', name);
      if (response.success) {
        toast.success('Category added successfully');
        // Invalidate variant categories cache
        await queryClient.invalidateQueries({ queryKey: ['upm-variant-categories'] });
        // Refetch categories to get the new one
        await refetchCategories();
        return response.data[0];
      } else {
        toast.error(response.message || 'Failed to add category');
        return null;
      }
    } catch (error) {
      console.error('Error adding category:', error);
      toast.error('Failed to add category');
      return null;
    }
  };

  // Add new value
  const addValue = async (valueName: string, categoryId: string): Promise<VariantValue | null> => {
    try {
      const response = await api.createUpmVariantValue(valueName, parseInt(categoryId));
      if (response.success) {
        toast.success('Value added successfully');
        // Invalidate variant values cache for this category
        await queryClient.invalidateQueries({ queryKey: ['upm-variant-values', categoryId] });
        // Refetch values to get the new one
        await refetchValues();
        return response.data[0];
      } else {
        toast.error(response.message || 'Failed to add value');
        return null;
      }
    } catch (error) {
      console.error('Error adding value:', error);
      toast.error('Failed to add value');
      return null;
    }
  };

  // Handle category selection
  const handleCategorySelect = (category: VariantCategory) => {
    setSelectedCategoryId(category.id);
    setShowAddCategory(false);
    setNewCategoryName('');
  };

  // Handle value selection
  const handleValueSelect = (selectedValue: VariantValue) => {
    if (!selectedCategoryId) return;

    const category = categories.find((c: VariantCategory) => c.id === selectedCategoryId);
    if (!category) return;

    // Check if this category is already used
    const categoryExists = value.some(v => v.category.id === category.id);

    if (categoryExists) {
      toast.error(`Category "${category.name}" is already selected. Please choose a different category.`);
      return;
    }

    // Check if this combination already exists (for safety)
    const exists = value.some(v =>
      v.category.id === category.id && v.value.id === selectedValue.id
    );

    if (!exists) {
      onChange([...value, { category, value: selectedValue }]);
    }

    setShowAddValue(false);
    setNewValueName('');
    setSelectedCategoryId(null);
  };

  // Handle adding new category
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;

    const newCategory = await addCategory(newCategoryName.trim());
    if (newCategory) {
      setNewCategoryName('');
      setShowAddCategory(false);
    }
  };

  // Handle adding new value
  const handleAddValue = async () => {
    if (!newValueName.trim() || !selectedCategoryId) return;

    const category = categories.find((c: VariantCategory) => c.id === selectedCategoryId);
    if (!category) return;

    // Check if this category is already used
    const categoryExists = value.some((v: VariantPair) => v.category.id === category.id);

    if (categoryExists) {
      toast.error(`Category "${category.name}" is already selected. Please choose a different category.`);
      return;
    }

    const newValue = await addValue(newValueName.trim(), selectedCategoryId);
    if (newValue) {
      // Auto-select the newly created value
      onChange([...value, { category, value: newValue }]);
      setNewValueName('');
      setShowAddValue(false);
      setSelectedCategoryId(null);
    }
  };

  // Handle variant removal
  const handleVariantRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };


  return (
    <div className={cn("space-y-4", className)}>
      {/* Display existing variants */}
      <div className="space-y-2">
        {value.map((variant, index) => (
          <div
            key={index}
            className="flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded-md"
          >
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm text-blue-800">{variant.category.name}:</span>
              <span className="text-sm text-blue-600">{variant.value.name}</span>
            </div>
            <button
              type="button"
              onClick={() => handleVariantRemove(index)}
              className="text-red-500 hover:text-red-700 p-1"
              disabled={disabled}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      {/* Add new variant */}
      <div className="space-y-3">
        {/* Category selection */}
        {!selectedCategoryId && (
          <div className="space-y-2">
            <div className="flex gap-2">
              <select
                value=""
                onChange={(e) => {
                  const categoryId = e.target.value;
                  if (categoryId) {
                    const category = categories.find((c: VariantCategory) => c.id === categoryId);
                    if (category) handleCategorySelect(category);
                  }
                }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-[#f0f0f0] text-[#444444] border-[#c4c4c4]"
                disabled={disabled}
              >
                <option value="">Select a category</option>
                {categories.length === 0 ? (
                  <option disabled>No categories available</option>
                ) : (
                  categories.map((category: VariantCategory) => {
                    const isUsed = value.some((v: VariantPair) => v.category.id === category.id);
                    return (
                      <option
                        key={category.id}
                        value={category.id}
                        disabled={isUsed}
                        className={isUsed ? 'text-gray-400' : ''}
                      >
                        {category.name} {isUsed ? '(Already used)' : ''}
                      </option>
                    );
                  })
                )}
              </select>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowAddCategory(true)}
                disabled={disabled}
                title="Add new category to master"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              Can't find the category you need? Click the + button to add it to the master.
            </p>

            {/* Add new category */}
            {showAddCategory && (
              <div className="flex gap-2">
                <Input
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Enter category name"
                  className="flex-1 text-sm bg-[#f0f0f0] text-[#444444] border-[#c4c4c4]"
                  disabled={disabled}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={disabled || !newCategoryName.trim()}
                  onClick={handleAddCategory}
                >
                  Add
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowAddCategory(false);
                    setNewCategoryName('');
                  }}
                  disabled={disabled}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Value selection */}
        {selectedCategoryId && (
          <div className="space-y-2">
            <div className="flex gap-2">
              <select
                value=""
                onChange={(e) => {
                  const valueId = parseInt(e.target.value);
                  if (valueId) {
                    const selectedValue = values.find((v: VariantValue) => v.id === valueId.toString());
                    if (selectedValue) handleValueSelect(selectedValue);
                  }
                }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-[#f0f0f0] text-[#444444] border-[#c4c4c4]"
                disabled={disabled}
              >
                <option value="">Select a value</option>
                {values.map((val: VariantValue) => (
                  <option key={val.id} value={val.id}>
                    {val.name}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowAddValue(true)}
                disabled={disabled}
                title="Add new value to master"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              Can't find the value you need? Click the + button to add it to the master.
            </p>

            {/* Add new value */}
            {showAddValue && (
              <div className="flex gap-2">
                <Input
                  value={newValueName}
                  onChange={(e) => setNewValueName(e.target.value)}
                  placeholder="Enter value"
                  className="flex-1 text-sm bg-[#f0f0f0] text-[#444444] border-[#c4c4c4]"
                  disabled={disabled}
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={handleAddValue}
                  disabled={disabled || !newValueName.trim()}
                >
                  Add
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowAddValue(false);
                    setNewValueName('');
                  }}
                  disabled={disabled}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedCategoryId(null);
                setShowAddValue(false);
                setNewValueName('');
              }}
              disabled={disabled}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default VariantInput;
